import {
  typography,
  size,
  colors,
  mediaQueries,
  InfoBlock as InfoBlockBase,
  TertiaryLink,
} from '@everlywell/leaves';
import { MarkdownContent } from 'components/Contentful/MarkdownContent';
import { animated } from 'react-spring';
import styled from 'styled-components';

const HeadlineText = styled.h3`
  ${typography.h3Text}
  font-size: ${size.lg}px;
  font-weight: ${typography.weight.book};
  line-height: ${size.xl1}px;
  text-align: left;
  color: ${colors.green5};
`;

const InfoBlock = styled(InfoBlockBase).attrs((props) => ({
  imageAlt: props.imageAlt,
}))`
  margin-bottom: ${size.xl1}px;
  background-color: transparent;

  ${mediaQueries.forTabletHorizontalUp} {
    margin-bottom: 0;
    background-color: transparent;
  }

  > div > div {
    background-color: transparent;
  }
`;

const AnimatedBlockContainer = styled(animated.div)`
  ${mediaQueries.forTabletHorizontalUp} {
    flex: 0 0 33%;
    &:not(:last-child) {
      padding-right: ${size.lg}px;
    }
  }
`;

const CategoryCardWrapper = styled.div.attrs(
  (props: { hasBadgeCallout: boolean }) => ({
    hasBadgeCallout: props.hasBadgeCallout,
  }),
)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  grid-auto-rows: minmax(100px, auto);
  margin-bottom: ${size.xl1}px;

  .gatsby-image-wrapper img {
    width: 70%;
  }

  ${mediaQueries.forTabletVerticalUp} {
    padding: ${({ hasBadgeCallout }) => (hasBadgeCallout ? '45' : '69')}px
      ${size.xl2}px ${size.xl2}px ${size.xl2}px;
    position: static;
    width: 410px;
    height: 639px;
    background: ${colors.white};
    border-radius: ${size.xs1}px;
  }

  ${mediaQueries.forTabletHorizontalUp} {
    ${({ hasBadgeCallout }) =>
      hasBadgeCallout &&
      `
      > div:nth-child(2) > div {
        margin-top: 60px;
      }
    `}
  }

  &:nth-child(even) > div:nth-child(1) {
    grid-column: 2 / 2;
    grid-row: 1;

    ${mediaQueries.forPhoneOnly} {
      margin-right: -22px;
      z-index: 1;
    }

    ${mediaQueries.forTabletVerticalUp} {
      grid-column: 1 / 3;
      grid-row: 1 / 5;
    }
  }

  &:nth-child(even) > div:nth-child(2) {
    grid-column: 1 / 2;
    grid-row: 1;

    ${mediaQueries.forPhoneOnly} {
      position: absolute;
      width: 300px;
      left: -130px;
    }

    ${mediaQueries.forTabletVerticalUp} {
      grid-column: 1 / 3;
      grid-row: 3 / 5;
      margin-top: ${size.xl7}px;
    }

    ${mediaQueries.forTabletVerticalDown} {
      margin-top: 28px;
    }
  }

  ${mediaQueries.forTabletHorizontalDown} {
    height: 650px;
  }

  ${mediaQueries.forTabletVerticalDown} {
    height: 540px;
    width: 100%;
    margin: ${size.lg}px auto;
  }

  ${mediaQueries.forPhoneOnly} {
    &:nth-child(even) {
      margin: -${size.xl1}px auto ${size.xl2}px auto;
    }
  }

  ${mediaQueries.forPhoneOnly} {
    height: 300px;

    &:nth-child(odd) > div:nth-child(2) {
      .gatsby-image-wrapper {
        position: absolute;
        width: 300px;
        left: 60px;
      }
    }
  }
`;

const InfoHeadlineContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  background-color: transparent;
  text-align: left;
  font-weight: ${typography.weight.light};
  padding-top: ${size.xs1}px;

  ${mediaQueries.forTabletHorizontalUp} {
    flex-direction: column;
    background-color: transparent;
    width: 95%;
    padding-top: ${size.sm}px;
  }

  ${AnimatedBlockContainer} {
    &:last-child {
      ${InfoBlock} {
        margin-bottom: 0;
      }
    }
  }
`;

const InfoText = styled.div`
  ${typography.buttonTextSmall}
  display: flex;
  font-size: ${size.md}px;
  font-weight: ${typography.weight.bold};
  line-height: ${size.lg}px;
  text-align: left;
  margin-bottom: ${size.sm}px;
`;

const InfoTextContainer = styled.div`
  margin-top: ${size.md}px;
`;

const Icon = styled.img`
  display: inline;
  width: ${size.sm}px;
  margin-right: ${size.xs1}px;
`;

const ShopButtonContainer = styled.div`
  ${typography.buttonTextSmall}
  display: flex;
  justify-content: start;
  align-items: center;
  margin-top: ${size.md}px;
  font-size: ${size.md}px;
  font-weight: ${parseInt(typography.weight.bold) + 100};
  cursor: pointer;
`;

const ShopButton = styled(TertiaryLink)`
  padding: ${size.sm}px 0;
  color: ${colors.green5};

  &:hover {
    color: ${colors.teal5};
  }
`;

const BadgeCallout = styled.div`
  ${typography.buttonTextSmall};
  color: ${colors.green5};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 ${size.xs1}px;
  border-style: solid;
  background-color: ${colors.green1};
  border: thin solid ${colors.green3};
  border-radius: 5px;
  max-width: fit-content;
`;

const HeadlineTextContainer = styled.div.attrs(
  (props: { positionRight: boolean }) => ({
    positionRight: props.positionRight,
  }),
)`
  display: flex;
  justify-content: ${(props) =>
    props.positionRight ? 'flex-end' : 'flex-start'};

  ${mediaQueries.forTabletHorizontalUp} {
    justify-content: flex-start;
  }
`;

const CardInfoWrapper = styled.div`
  grid-column: 1 / 2;
  grid-row: 1;

  ${mediaQueries.forPhoneOnly} {
    width: 220px;
  }

  ${mediaQueries.forTabletVerticalUp} {
    grid-column: 1 / 3;
    grid-row: 1 / 5;
  }
`;

const ProductImageWrapper = styled.div`
  grid-column: 2 / 2;
  grid-row: 1;

  ${mediaQueries.forTabletVerticalUp} {
    grid-column: 1 / 3;
    grid-row: 3 / 4;
    width: 300px;
    margin-top: 75px;
  }

  ${mediaQueries.forTabletHorizontalDown} {
    width: 100%;
    > div {
      width: 300px;
      margin-bottom: -100px;
      > div {
        margin-bottom: -100px;
        margin-top: 50px;
      }
    }
  }

  ${mediaQueries.forTabletVerticalDown} {
    grid-column: 1 / 3;
    grid-row: 3 / 4;
    margin-top: -18px;
    margin-left: 100px;
    width: 85%;

    > div {
      width: 500px;
      margin-top: -5px;
      > div {
        margin-bottom: 0;
        margin-top: 0;
      }
    }
  }

  ${mediaQueries.forPhoneOnly} {
    position: absolute;
    right: 0;
    flex-basis: unset;
    max-width: unset;
    overflow: hidden;
    width: 200px;
    height: 350px;
    margin-top: 10px;
  }
`;

const DesktopImage = styled.div`
  display: none;
  ${mediaQueries.forTabletVerticalUp} {
    display: block;
    width: 500px;
  }
`;

const MobileImage = styled.div`
  display: none;
  ${mediaQueries.forPhoneOnly} {
    display: block;
  }
`;

const BulletMarkdown = styled(MarkdownContent)`
  ${typography.buttonTextSmall}
`;

export {
  HeadlineText,
  CategoryCardWrapper,
  InfoHeadlineContainer,
  InfoText,
  InfoTextContainer,
  Icon,
  ShopButton,
  ShopButtonContainer,
  BadgeCallout,
  HeadlineTextContainer,
  CardInfoWrapper,
  ProductImageWrapper,
  BulletMarkdown,
  DesktopImage,
  MobileImage,
};
